import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_docs/docs/privacy-policy")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="mx-auto max-w-3xl py-8 sm:py-10">
      <h1 className="mb-6 font-bold text-2xl sm:text-3xl">Privacy Policy</h1>
      <p className="mb-8 text-gray-500 text-sm">Last updated: 2025-10-27</p>

      <p className="mb-6">
        This Privacy Policy describes how your personal information is
        collected, used, and shared when you visit or use the Open Farming
        Technologies FZ-LLC web application (the "Web App").
      </p>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">
        Personal Information We Collect
      </h2>
      <p className="mb-4">
        When you visit the Web App, we automatically collect certain information
        about your device, including information about your web browser, IP
        address, time zone, and some of the cookies that are installed on your
        device. Additionally, as you navigate through the Web App, we collect
        information about the individual web pages or products that you view,
        what websites or search terms referred you to the Web App, and
        information about how you interact with the Web App. We refer to this
        automatically-collected information as "Device Information."
      </p>
      <p className="mb-6">
        We also collect information that you provide to us, such as when you
        create an account or fill out a form on the Web App. This could include
        information such as your name, email address, phone number, address, and
        any other information you choose to provide ("Account Information").
      </p>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">
        How Do We Use Your Personal Information?
      </h2>
      <p className="mb-4">
        We use the Device Information that we collect to help us screen for
        potential risk and fraud (in particular, your IP address), and more
        generally to improve and optimize our Web App (for example, by
        generating analytics about how our customers browse and interact with
        the Web App, and to evaluate the success of our marketing and
        advertising campaigns).
      </p>
      <p className="mb-6">
        We use the Account Information that you provide to us to provide you
        with the functionality of the Web App, to communicate with you, and to
        provide you with information or advertising that may be of interest to
        you.
      </p>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">
        Sharing Your Personal Information
      </h2>
      <p className="mb-4">
        We share your Personal Information with third parties to help us use
        your Personal Information, as described above. For example, we use
        Google Analytics to help us understand how our customers use the Web
        App. Google Analytics has its own privacy policy, which you can review
        here:
      </p>
      <ul className="mb-6 ml-6 list-disc sm:ml-8">
        <li className="mb-2">
          <a
            href="https://policies.google.com/privacy"
            className="text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Privacy Policy
          </a>
        </li>
      </ul>
      <p className="mb-6">
        Finally, we may also share your Personal Information to comply with
        applicable laws and regulations, to respond to a subpoena, search
        warrant or other lawful requests for information we receive, or to
        otherwise protect our rights.
      </p>

      <h2 className="mt-10 mb-4 font-bold text-xl sm:text-2xl">
        How We Protect Google User Data
      </h2>
      <p className="mb-4">
        We access and store only the minimum Google user data required to
        provide our services:
      </p>
      <ul className="mb-4 ml-6 list-disc sm:ml-8">
        <li>OAuth tokens (to authenticate and retrieve messages)</li>
        <li>Unique identifiers for individual emails</li>
        <li>Attachments saved as PDF/PNG copies</li>
      </ul>
      <p className="mb-4">
        <span className="font-semibold">Secure Storage:</span> OAuth tokens and
        identifiers are stored securely in a PostgreSQL database. Attachments
        are stored in private, access-controlled Google Cloud Storage (GCP)
        buckets.
      </p>
      <p className="mb-4">
        <span className="font-semibold">Encryption:</span> All data is
        transmitted over HTTPS/TLS 1.2+ and encrypted at rest (e.g., AES-256).
      </p>
      <p className="mb-4">
        <span className="font-semibold">Access Control:</span> Access is limited
        to authorized backend services under the principle of least privilege
        and role-based access controls within our Django application.
      </p>
      <p className="mb-6">
        <span className="font-semibold">
          No Third-Party Selling or Unrelated Use:
        </span>{" "}
        We do not sell Google user data or use it for advertising. Data is used
        only to provide the OpenFarming features you request.
      </p>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">
        Data Retention and Deletion
      </h2>
      <p className="mb-4">
        We retain Google user data (OAuth tokens, email identifiers, and stored
        attachments) only as long as necessary to provide our services and
        fulfill the purposes described in this policy.
      </p>
      <ul className="mb-4 ml-6 list-disc sm:ml-8">
        <li>
          <span className="font-semibold">Revoke Access Anytime:</span> You can
          revoke the app’s access via your
          <a
            href="https://myaccount.google.com/permissions"
            className="text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            Google Account settings
          </a>
          .
        </li>
        <li>
          <span className="font-semibold">Deletion After Revocation:</span> When
          access is revoked, we delete associated OAuth tokens and Google user
          data from our systems within{" "}
          <span className="font-semibold">90 days</span>.
        </li>
        <li>
          <span className="font-semibold">Manual Deletion Requests:</span> You
          may request deletion at any time by emailing
          <a
            href="mailto:mhassan@openfarming.earth"
            className="text-blue-600 hover:underline"
          >
            {" "}
            mhassan@openfarming.earth
          </a>
          . We will permanently delete your Google user data (tokens,
          identifiers, and attachments) within{" "}
          <span className="font-semibold">90 days</span> of verifying your
          request.
        </li>
        <li>
          <span className="font-semibold">Automated Cleanup:</span> Expired or
          inactive tokens are periodically purged; attachments associated with
          revoked/expired access are also removed.
        </li>
      </ul>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">
        Compliance with Google API Services User Data Policy
      </h2>
      <p className="mb-4">
        Our use of information received from Google APIs adheres to the
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          className="text-blue-600 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {" "}
          Google API Services User Data Policy
        </a>
        , including the <span className="font-semibold">Limited Use</span>{" "}
        requirements. We only use Google user data to provide or improve
        user-facing features, do not transfer it except as necessary to provide
        the service, and do not use it for advertising.
      </p>

      <h2 className="mt-8 mb-4 font-bold text-xl sm:text-2xl">Contact</h2>
      <p className="mb-2">
        For questions or to request deletion of your data, please contact:
      </p>
      <p className="mb-6">
        <a
          href="mailto:mhassan@openfarming.earth"
          className="text-blue-600 hover:underline"
        >
          mhassan@openfarming.earth
        </a>
      </p>
    </div>
  );
}
